import { useErrorInlineNotification } from '@/components/ErrorInlineNotification';
import { ReportTypeEditTabs } from '@/features/report_types/components/ReportTypeEditTabs';
import { useCalculateTableHeight } from '@/features/transaction_items/hooks/useCalculateTableHeight';
import { useGetTableContainerHeight } from '@/features/transaction_items/hooks/useGetTableContainerHeight';
import { useConfirmBrowserNavigation } from '@/features/transaction_items/List/ReorderableTable/hooks/useConfirmBrowserNavigation';
import { useConfirmCancelReordering } from '@/features/transaction_items/List/ReorderableTable/hooks/useConfirmCancelReordering';
import { useConfirmLinkNavigation } from '@/features/transaction_items/List/ReorderableTable/hooks/useConfirmLinkNavigation';
import { useReorderingState } from '@/features/transaction_items/List/ReorderableTable/hooks/useReorderingState';
import { TransactionItemsList } from '@/features/transaction_items/List/TransactionItemsList';
import { useActiveCustomItemCountLimitFlag } from '@/features/transaction_items/featureFlags';
import { Loading } from '@moneyforward/ap-frontend-components';
import classNames from 'classnames';
import { FC, memo, useCallback, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import styles from './TransactionItems.module.scss';

const cx = classNames.bind(styles);

export const TransactionItems: FC = memo(({}) => {
  const isActiveCustomItemCountLimitEnabled =
    useActiveCustomItemCountLimitFlag();
  const [
    { setError },
    ErrorContextHolder,
    ErrorInlineNotificationProvider,
    onClearMessage,
  ] = useErrorInlineNotification();

  const containerHeight = useGetTableContainerHeight();
  const divRef = useRef<HTMLDivElement>(null);
  const tableBodyHeight = useCalculateTableHeight(
    containerHeight,
    divRef,
    ErrorContextHolder.props.visible
  );

  const { isReorderingMode, enterReorderingMode, exitReorderingMode } =
    useReorderingState();
  const [isFullScreenLoading, setIsFullScreenLoading] = useState(false);
  const [isOrderChanged, setIsOrderChanged] = useState(false);
  const showFullScreenLoading = useCallback((loading: boolean) => {
    setIsFullScreenLoading(loading);
  }, []);

  const style = useMemo(() => {
    return {
      height: containerHeight,
    };
  }, [containerHeight]);

  const { report_type_id } = useParams();
  const {
    confirm: confirmCancelReordering,
    ConfirmModal: ConfirmCancelReorderingModal,
  } = useConfirmCancelReordering();

  const { skipBrowserConfirm } = useConfirmBrowserNavigation(
    isReorderingMode && isOrderChanged
  );

  const [isDrawerIsOpen, setIsDrawerIsOpen] = useState(false);

  const confirmCancelReorderingWithSkip = useCallback(async () => {
    const isConfirmed = await confirmCancelReordering();
    if (isConfirmed) {
      skipBrowserConfirm();
    }
    return isConfirmed;
  }, [confirmCancelReordering, skipBrowserConfirm]);

  useConfirmLinkNavigation(
    isReorderingMode && isOrderChanged,
    confirmCancelReorderingWithSkip,
    skipBrowserConfirm
  );

  return (
    <ErrorInlineNotificationProvider value={setError}>
      {isFullScreenLoading && <Loading size='xlarge' isOverlay color='white' />}
      <ReportTypeEditTabs
        activeTab='transaction_items'
        reportTypeId={report_type_id ?? ''}
        showCustomFields
        isReorderingMode={isReorderingMode}
        isOrderChanged={isOrderChanged}
        confirmCancelReordering={confirmCancelReorderingWithSkip}
        skipBrowserConfirm={skipBrowserConfirm}
      />
      <div className={cx(styles['container'])} style={style} ref={divRef}>
        {ErrorContextHolder.props.visible && !isDrawerIsOpen && (
          <div className={cx(styles['inline-error'])}>{ErrorContextHolder}</div>
        )}
        <TransactionItemsList
          isActiveCustomItemCountLimitEnabled={
            isActiveCustomItemCountLimitEnabled
          }
          tableBodyHeight={tableBodyHeight}
          isReorderingMode={isReorderingMode}
          enterReorderingMode={enterReorderingMode}
          exitReorderingMode={exitReorderingMode}
          showFullScreenLoading={showFullScreenLoading}
          isFullScreenLoading={isFullScreenLoading}
          onOrderChange={setIsOrderChanged}
          isOrderChanged={isOrderChanged}
          confirmCancelReordering={confirmCancelReorderingWithSkip}
          setIsDrawerIsOpen={setIsDrawerIsOpen}
          notificationErrorContextHolder={ErrorContextHolder}
          notificationErrorVisible={ErrorContextHolder.props.visible}
          onClearNotificationErrorMessage={onClearMessage}
        />
        {isReorderingMode && <ConfirmCancelReorderingModal />}
      </div>
    </ErrorInlineNotificationProvider>
  );
});
TransactionItems.displayName = 'TransactionItems';
